import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, ApiError } from '../services/api'
import type { Usuario } from '../types'

interface AuthContextValue {
  usuario: Usuario | null
  iniciarSesion: (usuario: string, contrasena: string, empresa: string) => Promise<void>
  iniciarSesionPin: (pin: string, empresa: string, sucursal: number) => Promise<void>
  cerrarSesion: () => Promise<void>
  puede: (permiso: string) => boolean
  cargando: boolean
  inicializando: boolean
}

interface LoginResponse {
  token: string
  expiracion: string
  usuarioId: number
  nombre: string
  correo: string
  telefono: string
  tenantId: number
  tenantNombre: string
  subdominio: string
  roles: string[]
  permisos: string[]
}

const AuthContext = createContext<AuthContextValue | null>(null)

const STORAGE_USUARIO = 'logistica:usuario'
const STORAGE_TOKEN = 'logistica:token'
const STORAGE_PERMISOS = 'logistica:permisos'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null)
  const [cargando, setCargando] = useState(false)
  const [inicializando, setInicializando] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_TOKEN)
    const raw = localStorage.getItem(STORAGE_USUARIO)

    if (!token || !raw) {
      setInicializando(false)
      return
    }

    if (token.split('.').length !== 3) {
      localStorage.removeItem(STORAGE_TOKEN)
      localStorage.removeItem(STORAGE_PERMISOS)
      localStorage.removeItem(STORAGE_USUARIO)
      setInicializando(false)
      return
    }

    try {
      setUsuario(JSON.parse(raw) as Usuario)
    } catch {
      localStorage.removeItem(STORAGE_USUARIO)
    }
    setInicializando(false)

    api
      .get('/auth/verificar')
      .catch((error) => {
        if (error instanceof ApiError && error.status === 401) {
          localStorage.removeItem(STORAGE_TOKEN)
          localStorage.removeItem(STORAGE_PERMISOS)
          localStorage.removeItem(STORAGE_USUARIO)
          setUsuario(null)
        }
      })
  }, [])

  const iniciarSesion = async (usuario: string, contrasena: string, empresa: string) => {
    setCargando(true)
    try {
      const res = await api.post<LoginResponse>('/auth/login', {
        subdominio: empresa.toLowerCase().trim(),
        usuario,
        contrasena,
      })
      if (!res) throw new Error('No se recibio respuesta del servidor')

      localStorage.setItem(STORAGE_TOKEN, res.token)
      localStorage.setItem(STORAGE_PERMISOS, JSON.stringify(res.permisos ?? []))

      const u: Usuario = {
        id: res.usuarioId,
        tenantId: res.tenantId,
        nombre: res.nombre,
        telefono: res.telefono,
        correo: res.correo,
        activo: true,
        empresa: res.tenantNombre,
        nombreCompleto: res.nombre,
        rol: res.roles?.[0] ?? 'Usuario',
      }
      localStorage.setItem(STORAGE_USUARIO, JSON.stringify(u))
      setUsuario(u)
    } finally {
      setCargando(false)
    }
  }

  const iniciarSesionPin = async (_pin: string, _empresa: string, _sucursal: number) => {
    throw new Error('Inicio de sesion con PIN no esta implementado.')
  }

  const cerrarSesion = async () => {
    localStorage.removeItem(STORAGE_TOKEN)
    localStorage.removeItem(STORAGE_PERMISOS)
    localStorage.removeItem(STORAGE_USUARIO)
    setUsuario(null)
  }

  const puede = (permiso: string) => {
    const raw = localStorage.getItem(STORAGE_PERMISOS)
    const permisos: string[] = raw ? (JSON.parse(raw) as string[]) : []
    return permisos.includes(permiso) || permisos.includes('*')
  }

  return (
    <AuthContext.Provider value={{ usuario, iniciarSesion, iniciarSesionPin, cerrarSesion, puede, cargando, inicializando }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider')
  return ctx
}
